import * as Sentry from '@sentry/react-native';
import type { Session } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import { APP_RELEASE } from './app-release';
import { getClientEventContext } from './client-event-context';
import { resolveDevelopmentUrl } from './dev-url';
import { supabase } from './supabase';

export type ObservabilityScalar = string | number | boolean | null;

type ClientObservabilityInput = {
  kind: 'error' | 'event';
  eventName: string;
  route?: string | null;
  screen?: string | null;
  component?: string | null;
  message?: string | null;
  errorName?: string | null;
  errorMessage?: string | null;
  errorStack?: string | null;
  fingerprint?: string | null;
  stepNumber?: number | null;
  stepLabel?: string | null;
  metadata?: Record<string, ObservabilityScalar> | null;
};

type SerializedClientError = {
  errorName?: string;
  errorMessage?: string;
  errorStack?: string;
  fingerprint?: string;
};

type SentryEventLike = {
  message?: string;
  request?: {
    url?: string;
    headers?: Record<string, unknown>;
  };
  tags?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
    }>;
  };
};

const DEFAULT_BETTER_STACK_SENTRY_DSN =
  'https://h9fjxFPv4PmhwSGpajLxavxN@s2295349.eu-fsn-3.betterstackdata.com/2295349';
const BETTER_STACK_SENTRY_DSN =
  (Platform.OS === 'web'
    ? process.env['EXPO_PUBLIC_BETTER_STACK_DSN_WEB']
    : process.env['EXPO_PUBLIC_BETTER_STACK_DSN_NATIVE']) ??
  process.env['EXPO_PUBLIC_BETTER_STACK_DSN'] ??
  DEFAULT_BETTER_STACK_SENTRY_DSN;
const APP_ENVIRONMENT =
  process.env['EXPO_PUBLIC_APP_ENV'] ?? process.env['NODE_ENV'] ?? 'development';
const CLIENT_OBSERVABILITY_URL = `${resolveDevelopmentUrl(
  process.env['EXPO_PUBLIC_API_URL'] ?? 'http://localhost:3001',
).replace(/\/$/, '')}/observability/client`;
const SENTRY_LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);
const BOT_USER_AGENT_PATTERN =
  /\b(bot|crawler|spider|slurp|bingpreview|facebookexternalhit|google-inspectiontool)\b/i;

type GlobalWithSentryFlag = typeof globalThis & {
  __perdiemSentryInitialized?: boolean;
};

function getHeaderValue(
  headers: Record<string, unknown> | undefined,
  name: string,
): string | null {
  if (!headers) return null;
  const normalizedName = name.toLowerCase();
  const entry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === normalizedName,
  );
  const value = entry?.[1];
  return typeof value === 'string' ? value : null;
}

function getSentryEventMessage(event: SentryEventLike): string | null {
  const exceptionValue = event.exception?.values?.find((value) => value.value)?.value;
  return exceptionValue ?? event.message ?? null;
}

function hasLoopbackRequestUrl(event: SentryEventLike): boolean {
  if (!event.request?.url) return false;

  try {
    return SENTRY_LOOPBACK_HOSTS.has(new URL(event.request.url).hostname);
  } catch {
    return false;
  }
}

function hasBotUserAgent(event: SentryEventLike): boolean {
  const browserType = event.tags?.['browser.type'];
  if (typeof browserType === 'string' && browserType.toLowerCase() === 'bot') {
    return true;
  }

  const userAgent = getHeaderValue(event.request?.headers, 'user-agent');
  return userAgent !== null && BOT_USER_AGENT_PATTERN.test(userAgent);
}

function isExpectedMissingWorkspaceEvent(event: SentryEventLike): boolean {
  const metadata = event.contexts?.['ui_error_metadata'];
  const householdMissing = typeof metadata === 'object'
    && metadata !== null
    && (metadata as Record<string, unknown>)['householdMissing'] === true;

  return householdMissing && getSentryEventMessage(event) === "We couldn't find your workspace.";
}

function shouldDropSentryEvent(event: SentryEventLike): boolean {
  return hasLoopbackRequestUrl(event)
    || hasBotUserAgent(event)
    || isExpectedMissingWorkspaceEvent(event);
}

function ensureSentryInitialized() {
  const globalScope = globalThis as GlobalWithSentryFlag;
  if (globalScope.__perdiemSentryInitialized) {
    return;
  }

  Sentry.init({
    dsn: BETTER_STACK_SENTRY_DSN,
    enabled: !__DEV__,
    environment: APP_ENVIRONMENT,
    release: APP_RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend(event) {
      return shouldDropSentryEvent(event) ? null : event;
    },
  });

  globalScope.__perdiemSentryInitialized = true;
}

ensureSentryInitialized();

function sanitizeRoute(route: string | null | undefined): string | null {
  if (!route) return null;
  const trimmedRoute = route.trim();
  if (!trimmedRoute) return null;

  return trimmedRoute.split('?')[0]?.split('#')[0]?.slice(0, 200) ?? null;
}

function sanitizeText(
  value: string | null | undefined,
  maxLength: number,
): string | null {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  return trimmed.slice(0, maxLength);
}

function sanitizeMetadata(
  metadata: Record<string, ObservabilityScalar> | null | undefined,
): Record<string, ObservabilityScalar> | null {
  if (!metadata) return null;

  const entries = Object.entries(metadata)
    .filter(([key]) => key.trim().length > 0)
    .slice(0, 20)
    .map(([key, value]) => {
      if (typeof value === 'string') {
        return [key.slice(0, 80), value.slice(0, 500)] as const;
      }

      return [key.slice(0, 80), value] as const;
    });

  if (entries.length === 0) return null;

  return Object.fromEntries(entries);
}

function sanitizePayload(
  input: ClientObservabilityInput,
  sessionUserId: string | null,
): Record<string, unknown> {
  return {
    kind: input.kind,
    eventName: sanitizeText(input.eventName, 120) ?? 'client.unknown',
    route: sanitizeRoute(input.route),
    screen: sanitizeText(input.screen, 120),
    component: sanitizeText(input.component, 120),
    message: sanitizeText(input.message, 500),
    errorName: sanitizeText(input.errorName, 200),
    errorMessage: sanitizeText(input.errorMessage, 500),
    errorStack: sanitizeText(input.errorStack, 4000),
    fingerprint: sanitizeText(input.fingerprint, 200),
    stepNumber: input.stepNumber ?? null,
    stepLabel: sanitizeText(input.stepLabel, 120),
    metadata: sanitizeMetadata(input.metadata),
    sessionUserId,
    release: APP_RELEASE,
    environment: APP_ENVIRONMENT,
    platform: Platform.OS,
  };
}

export function updateBetterStackUser(session: Session | null) {
  ensureSentryInitialized();

  if (!session) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: session.user.id,
    email: session.user.email ?? undefined,
    username:
      typeof session.user.user_metadata?.['full_name'] === 'string'
        ? session.user.user_metadata['full_name']
        : undefined,
    isDemo:
      session.user.app_metadata?.['isDemo'] === true ? 'true' : 'false',
  });
}

export function updateBetterStackRoute(route: string | null | undefined) {
  ensureSentryInitialized();

  const sanitizedRoute = sanitizeRoute(route);
  if (!sanitizedRoute) return;

  Sentry.setTag('route', sanitizedRoute);
  Sentry.setContext('navigation', { route: sanitizedRoute });
}

export function reportUiError(params: {
  error?: unknown;
  message: string;
  route?: string | null;
  screen?: string;
  component?: string;
  metadata?: Record<string, ObservabilityScalar>;
}) {
  ensureSentryInitialized();

  const serializedError = serializeClientError(params.error);
  const sanitizedRoute = sanitizeRoute(params.route);
  const sanitizedMetadata = sanitizeMetadata(params.metadata);

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    if (sanitizedRoute) scope.setTag('route', sanitizedRoute);
    if (params.screen) scope.setTag('screen', params.screen);
    if (params.component) scope.setTag('component', params.component);
    if (serializedError.fingerprint) {
      scope.setFingerprint([serializedError.fingerprint]);
    }
    if (sanitizedMetadata) {
      scope.setContext('ui_error_metadata', sanitizedMetadata);
    }
    scope.setContext('ui_error', {
      message: params.message,
      ...(serializedError.errorName ? { errorName: serializedError.errorName } : {}),
      ...(serializedError.errorMessage
        ? { errorMessage: serializedError.errorMessage }
        : {}),
    });

    if (params.error instanceof Error) {
      Sentry.captureException(params.error);
      return;
    }

    if (serializedError.errorMessage) {
      Sentry.captureMessage(serializedError.errorMessage, 'error');
      return;
    }

    Sentry.captureMessage(params.message, 'error');
  });
}

export async function sendClientObservability(input: ClientObservabilityInput) {
  ensureSentryInitialized();

  const sessionResult = await supabase.auth.getSession();
  const session = sessionResult.data.session;
  const sessionUserId = session?.user.id ?? null;
  const clientEventContext = await getClientEventContext().catch(() => null);
  const payload = sanitizePayload(
    {
      ...input,
      metadata: {
        ...(clientEventContext ?? {}),
        ...(input.metadata ?? {}),
      },
    },
    sessionUserId,
  );

  Sentry.addBreadcrumb({
    category: input.kind === 'error' ? 'ui.error' : 'product.event',
    message: input.eventName,
    level: input.kind === 'error' ? 'error' : 'info',
    data: payload,
  });

  try {
    await fetch(CLIENT_OBSERVABILITY_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(session?.access_token
          ? { authorization: `Bearer ${session.access_token}` }
          : {}),
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // Ignore reporting failures so the original UI error remains the only
    // thing the user sees.
  }
}

export function serializeClientError(error: unknown): SerializedClientError {
  if (!error || typeof error !== 'object') {
    if (typeof error === 'string') {
      return {
        errorMessage: error.slice(0, 500),
        fingerprint: error.slice(0, 120),
      };
    }

    return {};
  }

  const maybeError = error as {
    name?: unknown;
    message?: unknown;
    stack?: unknown;
    data?: { code?: unknown };
  };

  const errorName =
    typeof maybeError.name === 'string' ? maybeError.name.slice(0, 200) : undefined;
  const errorMessage =
    typeof maybeError.message === 'string'
      ? maybeError.message.slice(0, 500)
      : undefined;
  const errorStack =
    typeof maybeError.stack === 'string'
      ? maybeError.stack.slice(0, 4000)
      : undefined;
  const errorCode =
    typeof maybeError.data?.code === 'string'
      ? maybeError.data.code.slice(0, 80)
      : undefined;

  return {
    ...(errorName ? { errorName } : {}),
    ...(errorMessage ? { errorMessage } : {}),
    ...(errorStack ? { errorStack } : {}),
    ...(errorCode || errorName || errorMessage
      ? {
          fingerprint: [errorCode, errorName, errorMessage]
            .filter(Boolean)
            .join(':')
            .slice(0, 200),
        }
      : {}),
  };
}
